import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { NuDsContext } from '@nubank/nuds-web/components/NuDSProvider/NuDSProvider';
import Link from '@nubank/nuds-web/components/Link/Link';
import NuLogo from '@nubank/nuds-web/components/NuLogo/NuLogo';
import { useBreakpointsMediaUp } from '@nubank/nuds-web/styles/breakpoints';

import Menu from 'components/Menu/Menu';
import RouterLink from 'components/RouterLink/RouterLink';
import { trackingClickMenu } from 'tracking/menu/clickInteraction';

import { ActionsWrapper } from './styles/ActionsWrapper';
import { AnchorWrapper } from './styles/AnchorWrapper';
import { ApplicationButton } from './styles/ApplicationButton';
import { StyledChip } from './styles/StyledChip';
import { LogoWrapper } from './styles/LogoWrapper';

function MenuWrapper({
  onToggleRegistrationForm,
  showNavigation,
  showLogin,
  showAnchor,
  isAnchorAllowed,
  isWidgetAllowed,
  anchorHandleClick,
  isRegistrationFormOpen,
  prospectTypeIntlKey,
  anchorIconName,
}) {
  const router = useRouter();
  const { route } = router;
  const { formatMessage } = useContext(NuDsContext);

  const isRouteChipHighligthed = routePath => (
    routePath === route
      ? 'primary.default'
      : 'black.light'
  );

  const breakpointsUpLG = useBreakpointsMediaUp('lg');

  const toggleMobileMenu = isMenuMobileOpen => {
    const eventLabel = isMenuMobileOpen ? 'open_menu' : 'close_menu';
    trackingClickMenu(eventLabel);
  };

  return (
    <Menu
      currentRoutePath={route}
      hideMobileMenu={!showNavigation}
      onToggleMobileMenu={isMenuMobileOpen => toggleMobileMenu(isMenuMobileOpen)}
      mobileButtonIconTitle={{
        titleClose: formatMessage({ id: 'COMMON.MENU.CLOSE_ACTION.TITLE' }),
        titleOpen: formatMessage({ id: 'COMMON.MENU.OPEN_ACTION.TITLE' }),
      }}
    >
      <Menu.Logo>
        {showNavigation ? (
          <LogoWrapper>
            <RouterLink
              href="/"
              onClick={() => trackingClickMenu('logo')}
            >
              <NuLogo variant="black" size="small" />
            </RouterLink>
          </LogoWrapper>
        ) : (
          <NuLogo variant="black" size="small" />
        )}
      </Menu.Logo>
      <Menu.Actions>
        <ActionsWrapper
          height="100%"
          width="100%"
          minWidth="100px"
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-end"
        >
          {(showLogin && breakpointsUpLG) && (
            <Link
              iconProps={{ name: 'login' }}
              href="https://app.nubank.com.br/"
              intlKey="HEADER.LOGIN.BUTTON"
              variant="action"
              onClick={() => trackingClickMenu('navigate_to_login')}
            />
          )}

          {isAnchorAllowed && (
            <AnchorWrapper
              data-testid="anchor-nav"
            >
              {showAnchor && (
                <Link
                  onClick={anchorHandleClick}
                  href="#quero-ser-cliente"
                  variant="action"
                  iconProps={{ name: anchorIconName, size: 'small' }}
                  intlKey={prospectTypeIntlKey}
                />
              )}
            </AnchorWrapper>
          )}

          {isWidgetAllowed && (
            <ApplicationButton
              id="widget-nav-toggle-button"
              onClick={onToggleRegistrationForm}
              aria-expanded={isRegistrationFormOpen}
              data-open-widget-button-index="1"
              intlKey={prospectTypeIntlKey}
            />
          )}
        </ActionsWrapper>
      </Menu.Actions>
      <Menu.ActionsSmallScreen>
        {({
          ActionLink,
        }) => (
          <>
            {showLogin && (
            <ActionLink
              iconProps={{
                name: 'login',
                title: 'login',
              }}
              href="https://app.nubank.com.br/"
              intlKey="HEADER.LOGIN.BUTTON"
              variant="action"
              onClick={() => trackingClickMenu('navigate_to_login')}
            />
            )}
          </>
        )}
      </Menu.ActionsSmallScreen>
      {showNavigation && (
        <Menu.Navigation>
          {({
            Section,
            SectionTitle,
            SectionLink,
            Column,
            ColumnTitle,
            ColumnLink,
          }) => (
            <>
              <Section>
                <SectionLink
                  href="/"
                  intlKey="HEADER.MENU.HOME"
                  onClick={() => trackingClickMenu('navigate_to_home')}
                />
              </Section>
              <Section>
                <SectionTitle intlKey="HEADER.MENU.FOR_YOU" />
                <Column>
                  <ColumnTitle intlKey="HEADER.MENU.FOR_YOU.NUCONTA" />
                  <ColumnLink
                    href="/conta"
                    onClick={() => trackingClickMenu('navigate_to_nuconta')}
                    intlKey="HEADER.MENU.FOR_YOU.NUCONTA"
                  />
                  <ColumnLink
                    href="/pix"
                    intlKey="HEADER.MENU.FOR_YOU.PIX"
                    forceRefresh
                    onClick={() => trackingClickMenu('navigate_to_pix')}
                    afterComponent={() => (
                      <StyledChip
                        backgroundColor="primary.defaultL10"
                        textColor={isRouteChipHighligthed('/pix')}
                        text={formatMessage({ id: 'HEADER.MENU.FOR_YOU.PIX.LABEL' })}
                      />
                    )}
                  />
                </Column>
                <Column>
                  <ColumnTitle intlKey="HEADER.MENU.FOR_YOU.CREDIT_CARD" />
                  <ColumnLink
                    href="/cartao"
                    intlKey="HEADER.MENU.FOR_YOU.CREDIT_CARD"
                    onClick={() => trackingClickMenu('navigate_to_credit_card')}
                  />
                  <ColumnLink
                    href="/rewards"
                    intlKey="HEADER.MENU.FOR_YOU.REWARDS"
                    onClick={() => trackingClickMenu('navigate_to_rewards')}
                  />
                </Column>
                <Column>
                  <ColumnTitle intlKey="HEADER.MENU.FOR_YOU.OTHER_PRODUCTS" />
                  <ColumnLink
                    href="/emprestimo"
                    intlKey="HEADER.MENU.FOR_YOU.LENDING"
                    onClick={() => trackingClickMenu('navigate_to_lending')}
                  />
                  <ColumnLink
                    href="/seguros"
                    intlKey="HEADER.MENU.FOR_YOU.INSURANCE"
                    onClick={() => trackingClickMenu('navigate_to_insurance')}
                    afterComponent={() => (
                      <StyledChip
                        backgroundColor="primary.defaultL10"
                        textColor={isRouteChipHighligthed('/seguros')}
                        text={formatMessage({ id: 'HEADER.MENU.FOR_YOU.INSURANCE.LABEL' })}
                      />
                    )}
                  />
                </Column>
              </Section>
              <Section>
                <SectionLink
                  href="/conta-pj"
                  intlKey="HEADER.MENU.FOR_YOUR_BUSINESS"
                  onClick={() => trackingClickMenu('navigate_to_business_account')}
                />
              </Section>
              <Section>
                <SectionTitle intlKey="HEADER.MENU.THE_NUBANK" />
                <Column>
                  <ColumnTitle intlKey="HEADER.MENU.THE_NUBANK.ABOUT_US" />
                  <ColumnLink
                    href="/sobre-nos"
                    intlKey="HEADER.MENU.THE_NUBANK.THE_NU"
                    onClick={() => trackingClickMenu('navigate_to_about_us')}
                  />
                  <ColumnLink
                    href="/imprensa"
                    intlKey="HEADER.MENU.THE_NUBANK.PRESS"
                    onClick={() => trackingClickMenu('navigate_to_press')}
                  />
                  <ColumnLink
                    href="/carreiras"
                    intlKey="HEADER.MENU.THE_NUBANK.CAREERS"
                    onClick={() => trackingClickMenu('navigate_to_careers')}
                  />
                  <ColumnLink
                    href="https://blog.nubank.com.br/impacto-social-nubank/"
                    intlKey="HEADER.MENU.THE_NUBANK.SOCIAL_IMPACT"
                    onClick={() => trackingClickMenu('navigate_to_blog_social_impact')}
                  />
                </Column>
                <Column>
                  <ColumnTitle intlKey="HEADER.MENU.THE_NUBANK.OTHER_CHANNELS" />
                  <ColumnLink
                    href="https://blog.nubank.com.br/"
                    intlKey="HEADER.MENU.THE_NUBANK.BLOG"
                    onClick={() => trackingClickMenu('navigate_to_blog')}
                  />
                  <ColumnLink
                    href="https://blog.nubank.com.br/assinar-newsletter/?utm_content=www_header-nlcta"
                    intlKey="HEADER.MENU.THE_NUBANK.NEWSLETTER"
                    onClick={() => trackingClickMenu('navigate_to_newsletter')}
                  />
                  <ColumnLink
                    href="https://comunidade.nubank.com.br/?utm_content=www_hero"
                    intlKey="HEADER.MENU.THE_NUBANK.NUCOMMUNITY"
                    onClick={() => trackingClickMenu('navigate_to_nucommunity')}
                  />
                </Column>
                <Column>
                  <ColumnTitle intlKey="HEADER.MENU.THE_NUBANK.NUBANK_AROUND_WORLD" />
                  <ColumnLink
                    href="https://nu.com.mx/"
                    intlKey="HEADER.MENU.THE_NUBANK.MX"
                    onClick={() => trackingClickMenu('navigate_to_mx')}
                  />
                  <ColumnLink
                    href="https://nu.com.co/"
                    intlKey="HEADER.MENU.THE_NUBANK.CO"
                    onClick={() => trackingClickMenu('navigate_to_co')}
                  />
                  <ColumnLink
                    href="https://nu.com.ar/"
                    intlKey="HEADER.MENU.THE_NUBANK.AR"
                    onClick={() => trackingClickMenu('navigate_to_ar')}
                  />
                </Column>
              </Section>
              <Section>
                <SectionLink
                  href="/perguntas"
                  intlKey="HEADER.MENU.FAQ"
                  onClick={() => trackingClickMenu('navigate_to_faq')}
                />
              </Section>
            </>
          )}
        </Menu.Navigation>
      )}
    </Menu>
  );
}

MenuWrapper.defaultProps = {
  anchorHandleClick: () => { },
  isAnchorAllowed: false,
  isWidgetAllowed: false,
  isRegistrationFormOpen: false,
  onToggleRegistrationForm: () => { },
  showAnchor: false,
  showLogin: false,
  showNavigation: false,
};

MenuWrapper.propTypes = {
  anchorHandleClick: PropTypes.func,
  anchorIconName: PropTypes.string.isRequired,
  isAnchorAllowed: PropTypes.bool,
  isRegistrationFormOpen: PropTypes.bool,
  isWidgetAllowed: PropTypes.bool,
  onToggleRegistrationForm: PropTypes.func,
  prospectTypeIntlKey: PropTypes.string.isRequired,
  showAnchor: PropTypes.bool,
  showLogin: PropTypes.bool,
  showNavigation: PropTypes.bool,
};

export default React.memo(MenuWrapper);
